import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteCatalogCustomerOrderDraft,
  fetchCatalogCustomerOrderDraft,
  saveCatalogCustomerOrderDraft,
} from "@/lib/catalogDraftApi";
import {
  clearDraftLocalStorage,
  createEmptyCustomerOrderDraft,
  loadDraftFromLocalStorage,
  sanitizeDraftForStorage,
  sanitizeDraftFromStorage,
  saveDraftToLocalStorage,
} from "@/lib/customerOrderDraft";

export function useCatalogCustomerOrderDraft({ user, token, enabled }) {
  const [draft, setDraftState] = useState(createEmptyCustomerOrderDraft);
  const [hydrated, setHydrated] = useState(false);
  const saveTimerRef = useRef(null);

  const clearPersistedDraft = useCallback(async () => {
    if (user?.id) {
      clearDraftLocalStorage(user.id);
    }
    if (token) {
      try {
        await deleteCatalogCustomerOrderDraft(token);
      } catch {
        // ignore network errors on cleanup
      }
    }
  }, [user?.id, token]);

  const setDraft = useCallback((updater) => {
    setDraftState((prev) => (typeof updater === "function" ? updater(prev) : updater));
  }, []);

  const resetDraft = useCallback(() => {
    setDraftState(createEmptyCustomerOrderDraft());
    clearPersistedDraft();
  }, [clearPersistedDraft]);

  useEffect(() => {
    if (!enabled || !user?.id) {
      setDraftState(createEmptyCustomerOrderDraft());
      setHydrated(false);
      return;
    }

    let cancelled = false;

    (async () => {
      let loaded = null;
      if (token) {
        try {
          const serverDraft = await fetchCatalogCustomerOrderDraft(token);
          if (serverDraft) {
            loaded = sanitizeDraftFromStorage(serverDraft);
          }
        } catch {
          // fallback to local storage below
        }
      }
      if (!loaded) {
        loaded = loadDraftFromLocalStorage(user.id);
      }
      if (!cancelled) {
        if (loaded) {
          setDraftState(loaded);
        }
        setHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, user?.id, token]);

  useEffect(() => {
    if (!hydrated || !enabled || !user?.id) return undefined;

    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const sanitized = sanitizeDraftForStorage(draft);
      saveDraftToLocalStorage(user.id, sanitized);
      if (token) {
        if (!sanitized.items.length && !sanitized.client) {
          deleteCatalogCustomerOrderDraft(token).catch(() => {});
        } else {
          saveCatalogCustomerOrderDraft(token, sanitized).catch(() => {});
        }
      }
    }, 600);

    return () => clearTimeout(saveTimerRef.current);
  }, [draft, hydrated, enabled, user?.id, token]);

  return {
    draft,
    setDraft,
    resetDraft,
    hydrated,
    clearPersistedDraft,
  };
}
